import React, { useContext } from 'react';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { parseAmountCents } from '@reverbdotcom/commons/src/money';

import QuickPriceEditButton, {
  VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT,
} from '../listings/quick_price_edit/quick_price_edit_button';
import SellerListingCardDetails from './SellerListingCardDetails';
import SellerListingCardFooter from './SellerListingCardFooter';
import BLANK_MEDIUM_IMAGE from '../../../images/products/blank_medium.jpg';
import PublishErrors from './PublishErrors';
import LoadingOverlay from '../../shared/loading_overlay';
import { RCCheckbox, RCImage } from '@reverbdotcom/cadence/components';
import SellerListingsCollectionContext from './SellerListingsCollectionContext';
import { SellerListing, ActionType } from './sellerListingsCollectionReducer';
import { isValidForBulkSelect } from './toolbarHelpers';
import classNames from 'classnames';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

interface IExternalProps {
  listing: SellerListing;
  showQuickPriceEditPopover?: boolean;
}

export const COMPONENT_NAME = 'SellerListingCard';

export default function SellerListingCard({
  listing,
  showQuickPriceEditPopover = false,
}: IExternalProps) {
  const user = useUser();
  const openNewTab = user && user.deviceName === WEB;
  const {
    useLazySellerListingsQuery,
  } = useContext(SellerListingsCollectionContext);

  const { refetchSellerListings } = useLazySellerListingsQuery();

  const {
    listingsCollectionState,
    dispatch: selectionDispatch,
  } = React.useContext(SellerListingsCollectionContext);

  const isSelected = !!listingsCollectionState.selectedListings.find(selectedListing => selectedListing.id == listing.id);

  const { shop } = listing;
  const defaultSellerPrice = listing.sellerPrice ?? parseAmountCents(0, shop.currency, { precision: 0 });

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: COMPONENT_NAME,
    listingId: listing.id,
  }, true);

  function handleSelection() {
    if (isSelected) {
      selectionDispatch({
        type: ActionType.DESELECT_LISTING,
        payload: {
          listing,
        },
      });
    } else {
      selectionDispatch({
        type: ActionType.SELECT_LISTING,
        payload: {
          listing,
        },
      });
    }
  }

  return (
    <div className="seller-listing-card">
      <LoadingOverlay showOverlay={listing.loading}>
        <div className="seller-listing-card__content">
          {isValidForBulkSelect(window) && (
            <RCCheckbox
              label=""
              checked={isSelected}
              onChange={() => handleSelection()}
            />
          )}

          <div className="seller-listing-card__content__image">
            <CoreLink
              to={listingItemPagePath(listing)}
              target={openNewTab ? '_blank' : undefined}
            >
              <RCImage
                src={listing.images[0]?.source || BLANK_MEDIUM_IMAGE}
                imageBox="fullWidth"
              />
            </CoreLink>
          </div>

          <div className="fx-grow">
            <div className="seller-listing-card__content__details-header">
              <CoreLink
                to={listingItemPagePath(listing)}
                target={openNewTab ? '_blank' : undefined}
              >
                {listing.title}
              </CoreLink>
              <div className="tablet-mt-3 tablet-fx-align-self-end" >
                <QuickPriceEditButton
                  listingId={listing.id}
                  defaultSellerPrice={defaultSellerPrice}
                  showQuickPriceEditPopover={showQuickPriceEditPopover}
                  userUuid={listing.sellerUuid}
                  vatIncluded={listing.vatIncluded}
                  vatIncludedHint={listing.vatIncludedHint}
                  isEditable={VALID_LISTING_STATES_FOR_QUICK_PRICE_EDIT.includes(listing.state)}
                  onSuccessCallback={() => refetchSellerListings([listing.id])}
                />
              </div>
            </div>

            <div className={classNames('seller-listing-card__content__details-content', {
              'seller-listing-card__content__details-content-with-checkbox': isValidForBulkSelect(window),
            })}
            >
              <SellerListingCardDetails listing={listing} />
              <PublishErrors listing={listing} />
            </div>
          </div>
        </div>
        <SellerListingCardFooter listing={listing}/>
      </LoadingOverlay>
    </div>
  );
}
